(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-product-main';

  function initSection(section) {
    if (!section || section.dataset.odditInit === 'true') return;
    section.dataset.odditInit = 'true';

    initVariantPills(section);
    initVariantSelector(section);
    initPiecesSelector(section);
    initWishlist(section);
    initReviewsScroll(section);
    initImageZoom(section);
    initVideoPlayback(section);
    initNotifyMe(section);
    initLowStockNotice(section);
    initAddToCart(section);
    initExtras(section);
    initStackCards(section);
    initStackSlider(section);
    initTraitsSlider(section);
    initExtrasSlider(section);
    initGallerySlider(section);
    initStickyAtc(section);
  }

  /* -------------------------------------------------------------------
     Gallery slider — mobile-only horizontal carousel (hero + thumbs).
     The arrows + dots live below the track and only render on mobile
     (CSS hides them on desktop). When desktop layout is active the
     track is `display: contents`, so the wheel listener and scrollLeft
     measurements no-op cleanly because there's no scrollable box.
     ------------------------------------------------------------------- */
  function initGallerySlider(section) {
    var track = section.querySelector('[data-oddit-gallery-track]');
    if (!track) return;
    var prevBtn = section.querySelector('[data-oddit-gallery-prev]');
    var nextBtn = section.querySelector('[data-oddit-gallery-next]');
    var dotsContainer = section.querySelector('[data-oddit-gallery-dots]');

    // Cards = hero + every visible thumb (the thumb-grid is `display: contents`
    // on mobile, so its children become direct flex items of the track).
    // Re-queried per call so variant-driven thumb hiding is reflected in stops.
    function getCards() {
      return section.querySelectorAll(
        '.oddit-section-product-main__hero, ' +
        '.oddit-section-product-main__thumb:not(.oddit-section-product-main__thumb--hidden)'
      );
    }

    function isCarouselActive() {
      // Only behave like a carousel when the track is laid out as one.
      return getComputedStyle(track).display === 'flex';
    }

    var DOT_SIZE = 9.6;
    var DOT_GAP = 8;

    function getStops() {
      if (!isCarouselActive()) return [0];
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      var cards = getCards();
      if (max <= 1 || !cards || cards.length === 0) return [0];
      var MIN_GAP = 60;
      var scrollPadLeft = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      var stops = [0];
      for (var i = 1; i < cards.length; i++) {
        var pos = cards[i].offsetLeft - scrollPadLeft;
        if (pos <= 0) continue;
        if (pos >= max - MIN_GAP) break;
        stops.push(pos);
      }
      stops.push(max);
      return stops;
    }

    // Active dot index = which stop the current scrollLeft is closest to.
    function activeDotIndex(stops) {
      var current = track.scrollLeft;
      var bestIdx = 0;
      var bestDist = Infinity;
      for (var i = 0; i < stops.length; i++) {
        var d = Math.abs(stops[i] - current);
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      }
      return bestIdx;
    }

    // The dots container holds an inner track. We translate the track so
    // the active dot is always centered in the 168px window, no matter
    // how many slides there are.
    function rebuildDots() {
      if (!dotsContainer) return;
      var stops = getStops();
      var idx = activeDotIndex(stops);

      var inner = dotsContainer.querySelector('.oddit-section-product-main__gallery-dots-track');
      if (!inner) {
        inner = document.createElement('span');
        inner.className = 'oddit-section-product-main__gallery-dots-track';
        dotsContainer.innerHTML = '';
        dotsContainer.appendChild(inner);
      }

      if (inner.childElementCount !== stops.length) {
        inner.innerHTML = '';
        for (var i = 0; i < stops.length; i++) {
          var dot = document.createElement('span');
          dot.className = 'oddit-section-product-main__gallery-dot';
          inner.appendChild(dot);
        }
      }

      var children = inner.children;
      for (var j = 0; j < children.length; j++) {
        if (j === idx) children[j].classList.add('oddit-section-product-main__gallery-dot--active');
        else children[j].classList.remove('oddit-section-product-main__gallery-dot--active');
      }

      // Sliding window: pin to left at the start, pin to right at the
      // end, otherwise keep the active dot centered. Track is positioned
      // at left:0, so translate = -(activeCenter - windowCenter) clamped
      // to [-(trackWidth - windowWidth), 0].
      var dotPitch = DOT_SIZE + DOT_GAP;
      var WINDOW_WIDTH = 168;
      var trackWidth = stops.length * dotPitch - DOT_GAP;
      var activeCenter = idx * dotPitch + DOT_SIZE / 2;
      var translate = -(activeCenter - WINDOW_WIDTH / 2);
      var minTranslate = Math.min(0, WINDOW_WIDTH - trackWidth);
      if (translate > 0) translate = 0;
      if (translate < minTranslate) translate = minTranslate;
      inner.style.transform = 'translateX(' + translate + 'px)';
    }

    function updateState() {
      if (!isCarouselActive()) return;
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 1;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= max - 1;
      rebuildDots();
    }

    function scrollByPage(direction) {
      if (!isCarouselActive()) return;
      var stops = getStops();
      var current = track.scrollLeft;
      var EPS = 4;
      var target;
      if (direction > 0) {
        target = stops[stops.length - 1];
        for (var i = 0; i < stops.length; i++) {
          if (stops[i] > current + EPS) { target = stops[i]; break; }
        }
      } else {
        target = stops[0];
        for (var j = stops.length - 1; j >= 0; j--) {
          if (stops[j] < current - EPS) { target = stops[j]; break; }
        }
      }
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollByPage(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollByPage(1); });
    track.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);
    section.addEventListener('oddit:gallery-recompute', updateState);

    updateState();
  }

  /* -------------------------------------------------------------------
     Traits marquee — continuous right-to-left loop on both desktop and
     mobile. Liquid renders one set of items; we duplicate them once so
     translating the track 0 → -50% (CSS @keyframes) wraps seamlessly.
     Duration is set from content width so visual speed is constant
     across viewports (~50px/sec). CSS handles pause-on-hover for
     fine-pointer devices and reduced-motion preference.
     ------------------------------------------------------------------- */
  function initTraitsSlider(section) {
    var slider = section.querySelector('.oddit-section-product-main__traits-slider');
    if (!slider) return;
    var track = slider.querySelector('[data-oddit-traits-track]');
    if (!track) return;
    var items = track.querySelectorAll('.oddit-section-product-main__trait');
    if (!items.length) return;

    var fragment = document.createDocumentFragment();
    for (var i = 0; i < items.length; i++) {
      var clone = items[i].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      fragment.appendChild(clone);
    }
    track.appendChild(fragment);

    var SPEED = 50; // px/sec
    function setDuration() {
      var halfWidth = track.scrollWidth / 2;
      if (halfWidth <= 0) return;
      track.style.setProperty('--oddit-traits-marquee-duration', (halfWidth / SPEED) + 's');
    }
    setDuration();
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(setDuration);
    }
    window.addEventListener('load', setDuration);
    window.addEventListener('resize', setDuration);
  }

  /* -------------------------------------------------------------------
     Extras slider — drag-to-scroll + arrow buttons + dot indicators
     ------------------------------------------------------------------- */
  function initExtrasSlider(section) {
    var slider = section.querySelector('.oddit-section-product-main__extras-slider');
    if (!slider) return;
    var track = slider.querySelector('[data-oddit-extras-track]');
    if (!track) return;

    var prevBtn = section.querySelector('[data-oddit-extras-prev]');
    var nextBtn = section.querySelector('[data-oddit-extras-next]');
    var dotsContainer = section.querySelector('[data-oddit-extras-dots]');
    var cards = track.querySelectorAll('.oddit-section-product-main__extra-card');

    // Bleed slider to the viewport's right edge regardless of parent padding/margin/grid
    function applyBleed() {
      slider.style.marginRight = '0px';
      var rect = slider.getBoundingClientRect();
      var bleed = window.innerWidth - rect.right;
      slider.style.marginRight = (bleed > 0 ? -bleed : 0) + 'px';
    }
    applyBleed();
    window.addEventListener('resize', applyBleed);

    // Build discrete scroll "stops" — one per card start, plus the true
    // max-scroll position as the final stop so the last card lands flush
    // against the right edge of the slider. Drop card-stops within
    // MIN_GAP of max (otherwise you'd get an awkward tiny final step).
    function getStops() {
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (max <= 1 || !cards || cards.length === 0) return [0];
      var MIN_GAP = 60;
      var stops = [0];
      for (var i = 1; i < cards.length; i++) {
        var pos = cards[i].offsetLeft;
        if (pos >= max - MIN_GAP) break;
        stops.push(pos);
      }
      stops.push(max);
      return stops;
    }

    // Index of the highest stop we've reached (i.e. passed or are sitting on).
    // Used for the active-dot indicator.
    function reachedStopIndex(stops) {
      var current = track.scrollLeft;
      var EPS = 4;
      for (var i = stops.length - 1; i >= 0; i--) {
        if (stops[i] <= current + EPS) return i;
      }
      return 0;
    }

    function rebuildDots() {
      if (!dotsContainer) return;
      var stops = getStops();
      var idx = reachedStopIndex(stops);
      dotsContainer.innerHTML = '';
      for (var i = 0; i < stops.length; i++) {
        var dot = document.createElement('span');
        dot.className = 'oddit-section-product-main__extras-dot';
        if (i === idx) dot.classList.add('oddit-section-product-main__extras-dot--active');
        dotsContainer.appendChild(dot);
      }
    }

    function updateState() {
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 1;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= max - 1;
      rebuildDots();
    }

    // Move to the next/previous stop *relative to* the current scroll position
    // (don't snap-to-nearest first, that caused the back arrow to jump weirdly
    // when scrolled mid-way between stops).
    function scrollByPage(direction) {
      var stops = getStops();
      var current = track.scrollLeft;
      var EPS = 4;
      var target;
      if (direction > 0) {
        target = stops[stops.length - 1];
        for (var i = 0; i < stops.length; i++) {
          if (stops[i] > current + EPS) { target = stops[i]; break; }
        }
      } else {
        target = stops[0];
        for (var j = stops.length - 1; j >= 0; j--) {
          if (stops[j] < current - EPS) { target = stops[j]; break; }
        }
      }
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollByPage(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollByPage(1); });
    track.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);

    // Pointer drag-to-scroll (desktop UX; touch keeps native scroll)
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;
    var DRAG_THRESHOLD = 4;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      // Don't start drag from inside a button (let click work)
      if (e.target.closest('[data-oddit-extra-add]')) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.style.cursor = 'grabbing';
      try { track.setPointerCapture(e.pointerId); } catch (_) {}
    });
    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) moved = true;
      if (moved) {
        e.preventDefault();
        track.scrollLeft = startScroll - dx;
      }
    });
    function endExtrasDrag(e) {
      if (!dragging) return;
      dragging = false;
      track.style.cursor = '';
      try { track.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    track.addEventListener('pointerup', endExtrasDrag);
    track.addEventListener('pointercancel', endExtrasDrag);
    track.addEventListener('pointerleave', endExtrasDrag);

    track.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);

    // Vertical wheel → horizontal scroll
    track.addEventListener('wheel', function (e) {
      if (e.deltaY === 0) return;
      var max = track.scrollWidth - track.clientWidth;
      if (max <= 1) return;
      var atStart = track.scrollLeft <= 0;
      var atEnd = track.scrollLeft >= max - 1;
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      e.preventDefault();
      track.scrollLeft += e.deltaY;
    }, { passive: false });

    updateState();
  }

  /* -------------------------------------------------------------------
     Variant pills (size, color, quantity)
     ------------------------------------------------------------------- */
  function initVariantPills(section) {
    var groups = section.querySelectorAll('.oddit-section-product-main__variant-options, .oddit-section-product-main__qty-options');

    groups.forEach(function (group) {
      var pills = group.querySelectorAll(
        '.oddit-section-product-main__pill, .oddit-section-product-main__qty-pill'
      );

      pills.forEach(function (pill) {
        pill.addEventListener('change', function () {
          syncCheckedClass(group);
          updateVariantValue(section, group);
        });
        pill.addEventListener('click', function () {
          // Allow the radio input to update first
          requestAnimationFrame(function () {
            syncCheckedClass(group);
            updateVariantValue(section, group);
          });
        });
      });
    });
  }

  function syncCheckedClass(group) {
    var pills = group.querySelectorAll(
      '.oddit-section-product-main__pill, .oddit-section-product-main__qty-pill'
    );
    pills.forEach(function (pill) {
      var input = pill.querySelector('.oddit-section-product-main__pill-input');
      if (!input) return;
      if (input.checked) {
        pill.classList.add(
          pill.classList.contains('oddit-section-product-main__qty-pill')
            ? 'oddit-section-product-main__qty-pill--checked'
            : 'oddit-section-product-main__pill--checked'
        );
      } else {
        pill.classList.remove(
          'oddit-section-product-main__pill--checked',
          'oddit-section-product-main__qty-pill--checked'
        );
      }
    });
  }

  function updateVariantValue(section, group) {
    var fieldset = group.closest('.oddit-section-product-main__variant');
    if (!fieldset) return;
    var valueEl = fieldset.querySelector('.oddit-section-product-main__variant-value');
    if (!valueEl) return;

    var checked = group.querySelector(
      '.oddit-section-product-main__pill-input:checked'
    );
    if (!checked) return;

    var labelEl = checked
      .closest('.oddit-section-product-main__pill, .oddit-section-product-main__qty-pill')
      .querySelector(
        '.oddit-section-product-main__pill-label, .oddit-section-product-main__qty-pill-name'
      );
    if (labelEl) {
      valueEl.textContent = labelEl.textContent.trim();
    }
  }

  /* -------------------------------------------------------------------
     Wishlist toggle
     ------------------------------------------------------------------- */
  function initWishlist(section) {
    var btn = section.querySelector('.oddit-section-product-main__wishlist');
    if (!btn) return;

    btn.addEventListener('click', function () {
      var pressed = btn.getAttribute('aria-pressed') === 'true';
      btn.setAttribute('aria-pressed', String(!pressed));
    });
  }

  /* -------------------------------------------------------------------
     Variant selector — resolves the selected option combo to a variant,
     then updates ATC button data-variant-id, price, and sold-out state.
     Reads variant data from the JSON blob rendered server-side.
     ------------------------------------------------------------------- */
  function initVariantSelector(section) {
    var dataEl = section.querySelector('[data-oddit-product-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var variants = (data && data.variants) || [];
    if (!variants.length) return;

    var atcBtn = section.querySelector('.oddit-section-product-main__add-to-cart[data-add-to-cart]:not(.oddit-section-product-main__add-to-cart--sticky)');
    var atcLabelEl = atcBtn && atcBtn.querySelector('.oddit-section-product-main__add-to-cart-text');
    var stickyBtn = section.querySelector('.oddit-section-product-main__add-to-cart--sticky[data-add-to-cart]');
    var stickyLabelEl = stickyBtn && stickyBtn.querySelector('[data-oddit-sticky-atc-label]');
    var stickyDefaultLabel = stickyLabelEl ? stickyLabelEl.textContent.trim() : 'Add';
    if (stickyDefaultLabel === 'Sold out') stickyDefaultLabel = 'Add';
    var stickyImageEl = section.querySelector('[data-oddit-sticky-atc-image]');
    var stickyVariantEl = section.querySelector('[data-oddit-sticky-atc-variant]');
    var stickyPriceEl = section.querySelector('[data-oddit-sticky-atc-price]');
    var priceEl = section.querySelector('.oddit-section-product-main__price');
    var pairFieldset = section.querySelector('[data-oddit-pieces-selector]');

    var heroImg = section.querySelector('.oddit-section-product-main__hero-image');
    var heroOriginal = heroImg ? {
      src: heroImg.getAttribute('src') || '',
      srcset: heroImg.getAttribute('srcset') || '',
      alt: heroImg.getAttribute('alt') || '',
      width: heroImg.getAttribute('width') || '',
      height: heroImg.getAttribute('height') || ''
    } : null;

    var initialLabel = atcLabelEl ? atcLabelEl.textContent : '';
    var labelPrefix = initialLabel.split('  -  ')[0] || initialLabel;

    var optionGroups = section.querySelectorAll(
      '.oddit-section-product-main__variant-options[role="radiogroup"]'
    );

    function getSelectedOptions() {
      var values = [];
      optionGroups.forEach(function (group) {
        if (group.getAttribute('aria-label') === 'Pieces') return;
        var checked = group.querySelector('input[type="radio"][name^="option-"]:checked');
        if (checked) values.push(checked.value);
      });
      return values;
    }

    function findVariant(selected) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (!v.options || v.options.length !== selected.length) continue;
        var match = true;
        for (var j = 0; j < selected.length; j++) {
          if (v.options[j] !== selected[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    function updatePairAvailability(variant) {
      if (!pairFieldset) return;
      var pairInput = pairFieldset.querySelector('input[data-pieces="2"]');
      if (!pairInput) return;
      var pairLabel = pairInput.closest('.oddit-section-product-main__pill');
      var unavailable = (
        variant.inventory_management === 'shopify' &&
        variant.inventory_policy !== 'continue' &&
        typeof variant.inventory_quantity === 'number' &&
        variant.inventory_quantity < 2
      );
      if (unavailable) {
        pairInput.disabled = true;
        if (pairLabel) pairLabel.classList.add('oddit-section-product-main__pill--unavailable');
        if (pairInput.checked) {
          var singleInput = pairFieldset.querySelector('input[data-pieces="1"]');
          if (singleInput) {
            singleInput.checked = true;
            singleInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      } else {
        pairInput.disabled = false;
        if (pairLabel) pairLabel.classList.remove('oddit-section-product-main__pill--unavailable');
      }
    }

    function applyHeroImage(variant) {
      if (!heroImg) return;
      var img = variant && variant.featured_image;
      if (img && img.src) {
        heroImg.setAttribute('src', img.src);
        if (img.srcset) heroImg.setAttribute('srcset', img.srcset);
        else heroImg.removeAttribute('srcset');
        if (img.alt != null) heroImg.setAttribute('alt', img.alt);
        if (img.width) heroImg.setAttribute('width', img.width);
        if (img.height) heroImg.setAttribute('height', img.height);
      } else if (heroOriginal) {
        heroImg.setAttribute('src', heroOriginal.src);
        if (heroOriginal.srcset) heroImg.setAttribute('srcset', heroOriginal.srcset);
        else heroImg.removeAttribute('srcset');
        heroImg.setAttribute('alt', heroOriginal.alt);
        if (heroOriginal.width) heroImg.setAttribute('width', heroOriginal.width);
        if (heroOriginal.height) heroImg.setAttribute('height', heroOriginal.height);
      }
    }

    // Mirror of Liquid's `| handleize` for client-side comparisons.
    function handleize(str) {
      return String(str == null ? '' : str)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function applyThumbVisibility(variant) {
      if (data.color_option_index == null) return;
      var rawValue = variant && variant.options ? variant.options[data.color_option_index] : '';
      var activeColor = handleize(rawValue || '');
      var thumbs = section.querySelectorAll('[data-oddit-thumb-colors]');
      thumbs.forEach(function (thumb) {
        var raw = thumb.getAttribute('data-oddit-thumb-colors') || '';
        var visible = raw === '' || raw.split(',').indexOf(activeColor) !== -1;
        thumb.classList.toggle('oddit-section-product-main__thumb--hidden', !visible);
      });
      section.dispatchEvent(new CustomEvent('oddit:gallery-recompute'));
    }

    function updateUrl(variantId) {
      if (!window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', String(variantId));
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* malformed URL — skip */ }
    }

    function findVariantById(id) {
      if (id == null) return null;
      var key = String(id);
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === key) return variants[i];
      }
      return null;
    }

    // Reads the live variant + quantity off the ATC button so the label can be
    // refreshed independently from variant changes (e.g. when the Single/Pair
    // pieces selector toggles `data-quantity`).
    function refreshAtcLabel() {
      if (!atcBtn || !atcLabelEl) return;
      var variant = findVariantById(atcBtn.getAttribute('data-variant-id'));
      if (!variant) return;
      if (!variant.available) {
        atcBtn.disabled = true;
        atcLabelEl.textContent = 'Sold out';
        return;
      }
      atcBtn.disabled = false;
      var qty = parseInt(atcBtn.getAttribute('data-quantity'), 10);
      var priceText = qty === 2 ? variant.price_pair_formatted : variant.price_formatted;
      atcLabelEl.textContent = labelPrefix + '  -  ' + priceText;
    }

    section.addEventListener('oddit:pieces-change', refreshAtcLabel);

    function applyVariant(variant) {
      if (!variant) {
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.removeAttribute('data-variant-id');
          if (atcLabelEl) atcLabelEl.textContent = 'Unavailable';
        }
        if (stickyBtn) {
          stickyBtn.disabled = true;
          stickyBtn.removeAttribute('data-variant-id');
          if (stickyLabelEl) stickyLabelEl.textContent = 'Sold out';
        }
        // TL-108: tell the buy-buttons wrap there's no usable variant so
        // it can fall back to the notify-me CTA.
        section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: null } }));
        return;
      }
      if (atcBtn) {
        atcBtn.setAttribute('data-variant-id', String(variant.id));
      }
      if (stickyBtn) {
        stickyBtn.setAttribute('data-variant-id', String(variant.id));
        stickyBtn.disabled = !variant.available;
        if (stickyLabelEl) {
          stickyLabelEl.textContent = variant.available ? stickyDefaultLabel : 'Sold out';
        }
      }
      refreshAtcLabel();
      if (priceEl) priceEl.textContent = variant.price_formatted;
      if (stickyPriceEl) stickyPriceEl.textContent = variant.price_formatted;
      if (stickyVariantEl && variant.options) {
        stickyVariantEl.textContent = variant.options.join(' | ');
      }
      if (stickyImageEl && variant.featured_image && variant.featured_image.src) {
        stickyImageEl.setAttribute('src', variant.featured_image.src);
        stickyImageEl.removeAttribute('srcset');
        if (variant.featured_image.alt != null) {
          stickyImageEl.setAttribute('alt', variant.featured_image.alt);
        }
      }
      updatePiecesPrices(variant);
      updatePairAvailability(variant);
      updateSwatchAvailability();
      applyHeroImage(variant);
      applyThumbVisibility(variant);
      updateUrl(variant.id);
      // TL-108: let initNotifyMe (and any other listener) react to
      // the new variant's availability — flips the wrap's
      // data-variant-available attribute, which the CSS uses to
      // swap between ATC and Notify-Me buttons.
      section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant } }));
    }

    function getOptionPosition(input) {
      var match = input.name.match(/^option-(\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    }

    function updateSwatchAvailability() {
      var swatchGroups = section.querySelectorAll('[data-oddit-swatch-option]');
      if (!swatchGroups.length) return;

      var siblingSelections = [];
      optionGroups.forEach(function (otherGroup) {
        if (otherGroup.getAttribute('aria-label') === 'Pieces') return;
        if (otherGroup.hasAttribute('data-oddit-swatch-option')) return;
        var checked = otherGroup.querySelector('input[type="radio"][name^="option-"]:checked');
        if (!checked) return;
        var pos = getOptionPosition(checked);
        if (pos == null) return;
        siblingSelections.push({ idx: pos - 1, value: checked.value });
      });

      swatchGroups.forEach(function (group) {
        var firstRadio = group.querySelector('input[type="radio"][name^="option-"]');
        if (!firstRadio) return;
        var pos = getOptionPosition(firstRadio);
        if (pos == null) return;
        var thisIdx = pos - 1;

        var pills = group.querySelectorAll('.oddit-section-product-main__pill');
        pills.forEach(function (pill) {
          var input = pill.querySelector('input[type="radio"]');
          if (!input) return;
          var thisValue = input.value;

          var anyAvailable = false;
          for (var i = 0; i < variants.length; i++) {
            var v = variants[i];
            if (!v.options || !v.available) continue;
            if (v.options[thisIdx] !== thisValue) continue;
            var siblingsMatch = true;
            for (var j = 0; j < siblingSelections.length; j++) {
              var s = siblingSelections[j];
              if (v.options[s.idx] !== s.value) { siblingsMatch = false; break; }
            }
            if (siblingsMatch) { anyAvailable = true; break; }
          }

          if (anyAvailable) {
            pill.classList.remove('oddit-section-product-main__pill--unavailable');
          } else {
            pill.classList.add('oddit-section-product-main__pill--unavailable');
          }
        });
      });
    }

    function updatePiecesPrices(variant) {
      var nodes = section.querySelectorAll('[data-oddit-pieces-price]');
      nodes.forEach(function (node) {
        var pieces = node.getAttribute('data-pieces');
        if (pieces === '2') {
          node.textContent = variant.price_pair_formatted;
        } else {
          node.textContent = variant.price_formatted;
        }
      });
    }

    optionGroups.forEach(function (group) {
      if (group.getAttribute('aria-label') === 'Pieces') return;
      group.addEventListener('change', function () {
        applyVariant(findVariant(getSelectedOptions()));
      });
    });

    updateSwatchAvailability();
  }

  /* -------------------------------------------------------------------
     Pieces selector (Single / Pair) — drives qty + line-item property.
     The hidden properties[_pieces] input is read by initAddToCart along
     with data-quantity on the ATC button.
     ------------------------------------------------------------------- */
  function initPiecesSelector(section) {
    var fieldset = section.querySelector('[data-oddit-pieces-selector]');
    if (!fieldset) return;

    var atcBtn = section.querySelector('.oddit-section-product-main__add-to-cart[data-add-to-cart]');
    var hiddenProp = fieldset.querySelector('[data-oddit-pieces-property]');
    var radios = fieldset.querySelectorAll('input[type="radio"][data-pieces]');

    function sync(radio) {
      var pieces = radio.getAttribute('data-pieces') || '1';
      var label = radio.value || 'Single';
      if (hiddenProp) hiddenProp.value = label;
      if (atcBtn) atcBtn.setAttribute('data-quantity', pieces);
      section.dispatchEvent(new CustomEvent('oddit:pieces-change'));
    }

    radios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (r.checked) sync(r);
      });
    });

    var initial = fieldset.querySelector('input[type="radio"][data-pieces]:checked');
    if (initial) sync(initial);
  }

  function collectProperties(section) {
    var inputs = section.querySelectorAll('input[name^="properties["]');
    var props = {};
    inputs.forEach(function (input) {
      var match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      var key = match[1];
      var value = input.value;
      if (value === '' || value == null) return;
      props[key] = value;
    });
    return props;
  }

  /* -------------------------------------------------------------------
     Main add-to-cart — async, opens cart drawer with rendered section
     ------------------------------------------------------------------- */
  function initAddToCart(section) {
    var btns = section.querySelectorAll('.oddit-section-product-main__add-to-cart[data-add-to-cart]');
    if (!btns.length) return;

    btns.forEach(function (btn) {
      var labelEl = btn.querySelector(
        '.oddit-section-product-main__add-to-cart-text, [data-oddit-sticky-atc-label]'
      );
      bindAddToCart(section, btn, labelEl);
    });
  }

  function bindAddToCart(section, btn, labelEl) {
    btn.addEventListener('click', function () {
      if (btn.disabled) return;

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      // Pieces selector lives on the in-page button only — read it from there
      // so the sticky ATC stays in sync with the user's Single/Pair choice.
      var qtySource = section.querySelector(
        '.oddit-section-product-main__add-to-cart[data-add-to-cart]:not(.oddit-section-product-main__add-to-cart--sticky)'
      ) || btn;
      var quantity = parseInt(qtySource.getAttribute('data-quantity'), 10);
      if (!quantity || quantity < 1) quantity = 1;

      var props = collectProperties(section);
      var item = { id: Number(variantId), quantity: quantity };
      if (Object.keys(props).length) item.properties = props;

      var originalLabel = labelEl ? labelEl.textContent : btn.textContent;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (labelEl) labelEl.textContent = 'Adding...';
      else btn.textContent = 'Adding...';

      var cartApi = window.OdditCartDrawer;
      var addPromise;
      if (cartApi && typeof cartApi.addToCart === 'function') {
        addPromise = cartApi.addToCart(Number(variantId), quantity, item.properties || {});
      } else {
        addPromise = fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [item],
            sections: 'oddit-cart-drawer',
            sections_url: window.location.pathname
          })
        })
          .then(function (resp) {
            return resp.json().catch(function () { return {}; }).then(function (data) {
              if (!resp.ok) {
                var msg = (data && (data.description || data.message)) || 'Add to cart failed';
                var err = new Error(msg);
                err.data = data;
                throw err;
              }
              return data;
            });
          })
          .then(function (data) {
            var fbApi = window.OdditCartDrawer;
            if (fbApi) {
              var sectionHTML = data && data.sections && data.sections['oddit-cart-drawer'];
              var rendered = false;
              if (sectionHTML && typeof fbApi.renderFromSection === 'function') {
                rendered = !!fbApi.renderFromSection(sectionHTML);
              }
              if (typeof fbApi.open === 'function') {
                fbApi.open(rendered ? { skipRefresh: true } : undefined);
              }
            }
            return data;
          });
      }

      addPromise
        .then(function () {
          btn.removeAttribute('aria-busy');
          if (labelEl) labelEl.textContent = 'Added';
          else btn.textContent = 'Added';
          setTimeout(function () {
            btn.disabled = false;
            if (labelEl) labelEl.textContent = originalLabel;
            else btn.textContent = originalLabel;
          }, 1500);
        })
        .catch(function (err) {
          console.error('[oddit-section-product-main] add to cart failed', err);
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
          if (labelEl) labelEl.textContent = originalLabel;
          else btn.textContent = originalLabel;
        });
    });
  }

  /* -------------------------------------------------------------------
     Extras — async add-to-cart for cross-upsell products
     ------------------------------------------------------------------- */
  function initExtras(section) {
    var addBtns = section.querySelectorAll('[data-oddit-extra-add]');
    addBtns.forEach(function (btn) {
      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', function () {
        if (btn.disabled) return;

        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        var originalLabel = btn.textContent;
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Adding...';

        // TL-98: route extras through the cart drawer's addToCart so
        // the free gift bundles into the same /cart/add.js request.
        var cartApi = window.OdditCartDrawer;
        var addPromise;
        if (cartApi && typeof cartApi.addToCart === 'function') {
          addPromise = cartApi.addToCart(Number(variantId), 1);
        } else {
          addPromise = fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }],
              sections: 'oddit-cart-drawer',
              sections_url: window.location.pathname
            })
          })
            .then(function (resp) {
              return resp.json().catch(function () { return {}; }).then(function (data) {
                if (!resp.ok) {
                  var msg = (data && (data.description || data.message)) || 'Add to cart failed';
                  var err = new Error(msg);
                  err.data = data;
                  throw err;
                }
                return data;
              });
            })
            .then(function (data) {
              var fbApi = window.OdditCartDrawer;
              if (fbApi) {
                var sectionHTML = data && data.sections && data.sections['oddit-cart-drawer'];
                var rendered = false;
                if (sectionHTML && typeof fbApi.renderFromSection === 'function') {
                  rendered = !!fbApi.renderFromSection(sectionHTML);
                }
                if (typeof fbApi.open === 'function') {
                  fbApi.open(rendered ? { skipRefresh: true } : undefined);
                }
              }
              return data;
            });
        }

        addPromise
          .then(function () {
            btn.removeAttribute('aria-busy');
            btn.setAttribute('aria-pressed', 'true');
            btn.textContent = 'Added';
            setTimeout(function () {
              btn.disabled = false;
              btn.setAttribute('aria-pressed', 'false');
              btn.textContent = originalLabel;
            }, 1500);
          })
          .catch(function (err) {
            console.error('[oddit-section-product-main] extras add to cart failed', err);
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            btn.textContent = originalLabel;
          });
      });
    });
  }

  /* -------------------------------------------------------------------
     Build Your Ear Stack — toggle add-on cards, recompute total + count
     ------------------------------------------------------------------- */
  function initStackCards(section) {
    var stack = section.querySelector('[data-oddit-stack]');
    if (!stack) return;
    var cards = stack.querySelectorAll('.oddit-section-product-main__stack-card');
    var totalEl = stack.querySelector('[data-oddit-stack-total]');
    var saveEl = stack.querySelector('[data-oddit-stack-save]');
    var countEl = stack.querySelector('[data-oddit-stack-count]');
    var subEl = stack.querySelector('[data-oddit-stack-sub]');

    var DISCOUNT_THRESHOLD = 2; // # of add-ons selected to trigger discount
    var DISCOUNT_PERCENT = 15;

    function update() {
      var selected = 0;
      var total = 0;
      var addOnsSelected = 0;
      cards.forEach(function (card) {
        var isCurrent = card.classList.contains('oddit-section-product-main__stack-card--current');
        var isSelected = isCurrent || card.classList.contains('oddit-section-product-main__stack-card--selected');
        if (isSelected) {
          selected += 1;
          var price = parseFloat(card.getAttribute('data-stack-price') || '0');
          if (!isNaN(price)) total += price;
          if (!isCurrent) addOnsSelected += 1;
        }
      });

      var applyDiscount = addOnsSelected >= DISCOUNT_THRESHOLD;
      var discounted = applyDiscount ? total * (1 - DISCOUNT_PERCENT / 100) : total;

      if (totalEl) totalEl.textContent = '$' + discounted.toFixed(2);
      if (saveEl) {
        saveEl.textContent = 'SAVE ' + DISCOUNT_PERCENT + '%';
        saveEl.style.visibility = applyDiscount ? 'visible' : 'hidden';
      }
      if (countEl) countEl.textContent = 'Add ' + selected + (selected === 1 ? ' item to cart' : ' items to cart');
      if (subEl) {
        var remaining = Math.max(0, DISCOUNT_THRESHOLD - addOnsSelected);
        if (remaining > 0) {
          subEl.textContent = 'Add ' + remaining + ' more earring' + (remaining === 1 ? '' : 's') + ' and save ' + DISCOUNT_PERCENT + '%';
        } else {
          subEl.textContent = "You're saving " + DISCOUNT_PERCENT + '% on this stack';
        }
      }
    }

    function setCardSelected(card, nowSelected) {
      card.classList.toggle('oddit-section-product-main__stack-card--selected', nowSelected);
      var btn = card.querySelector('[data-oddit-stack-toggle]');
      if (btn) btn.setAttribute('aria-pressed', String(nowSelected));
    }

    var toggles = stack.querySelectorAll('[data-oddit-stack-toggle]');
    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.oddit-section-product-main__stack-card');
        if (!card) return;
        var nowSelected = !card.classList.contains('oddit-section-product-main__stack-card--selected');
        setCardSelected(card, nowSelected);
        update();
      });
    });

    stack.addEventListener('click', function (e) {
      var imageBox = e.target.closest('.oddit-section-product-main__stack-card-image');
      if (!imageBox) return;
      if (e.target.closest('[data-oddit-stack-toggle]')) return;
      if (e.target.closest('a')) return;
      var card = imageBox.closest('.oddit-section-product-main__stack-card');
      if (!card) return;
      if (card.classList.contains('oddit-section-product-main__stack-card--current')) return;
      if (card.classList.contains('oddit-section-product-main__stack-card--unavailable')) return;
      var nowSelected = !card.classList.contains('oddit-section-product-main__stack-card--selected');
      setCardSelected(card, nowSelected);
      update();
    });

    stack.addEventListener('click', function (e) {
      var swatch = e.target.closest('[data-oddit-stack-swatch]');
      if (!swatch) return;
      e.preventDefault();
      e.stopPropagation();
      if (swatch.getAttribute('aria-disabled') === 'true') return;

      var group = swatch.closest('.oddit-section-product-main__stack-card-swatches');
      if (group) {
        group.querySelectorAll('.oddit-section-product-main__stack-card-swatch--selected').forEach(function (s) {
          s.classList.remove('oddit-section-product-main__stack-card-swatch--selected');
          s.setAttribute('aria-checked', 'false');
        });
      }
      swatch.classList.add('oddit-section-product-main__stack-card-swatch--selected');
      swatch.setAttribute('aria-checked', 'true');

      var card = swatch.closest('.oddit-section-product-main__stack-card');
      if (!card) return;

      var variantId = swatch.getAttribute('data-variant-id');
      if (variantId) card.setAttribute('data-variant-id', variantId);

      var swatchImage = swatch.getAttribute('data-swatch-image');
      if (swatchImage) {
        var img = card.querySelector('.oddit-section-product-main__stack-card-image img');
        if (img) {
          img.src = swatchImage;
          var swatchSrcset = swatch.getAttribute('data-swatch-srcset');
          if (swatchSrcset) img.srcset = swatchSrcset;
        }
      }
    });

    var addBtn = stack.querySelector('[data-oddit-stack-add]');
    var addLabel = addBtn ? addBtn.querySelector('[data-oddit-stack-count]') : null;
    var errorEl = stack.querySelector('[data-oddit-stack-error]');

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        if (addBtn.disabled) return;
        clearError();

        var items = [];
        cards.forEach(function (card) {
          if (card.classList.contains('oddit-section-product-main__stack-card--unavailable')) return;
          var isCurrent = card.classList.contains('oddit-section-product-main__stack-card--current');
          var isSelected = isCurrent || card.classList.contains('oddit-section-product-main__stack-card--selected');
          if (!isSelected) return;
          var variantId = card.getAttribute('data-variant-id');
          if (!variantId) return;
          items.push({ id: Number(variantId), quantity: 1 });
        });
        if (items.length === 0) {
          showError('Select at least one item to add to your stack.');
          return;
        }

        addBtn.disabled = true;
        addBtn.setAttribute('aria-busy', 'true');
        if (addLabel) addLabel.textContent = 'Adding...';

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: items,
            sections: 'oddit-cart-drawer',
            sections_url: window.location.pathname
          })
        })
          .then(function (resp) {
            return resp.json().catch(function () { return {}; }).then(function (data) {
              if (!resp.ok) {
                var msg = (data && (data.description || data.message)) || 'Add to cart failed';
                var err = new Error(msg);
                err.data = data;
                throw err;
              }
              return data;
            });
          })
          .then(function (data) {
            addBtn.removeAttribute('aria-busy');
            if (addLabel) addLabel.textContent = 'Added';

            var cartApi = window.OdditCartDrawer;
            if (cartApi) {
              var sectionHTML = data && data.sections && data.sections['oddit-cart-drawer'];
              var rendered = false;
              if (sectionHTML && typeof cartApi.renderFromSection === 'function') {
                rendered = !!cartApi.renderFromSection(sectionHTML);
              }
              if (typeof cartApi.open === 'function') {
                cartApi.open(rendered ? { skipRefresh: true } : undefined);
              }
            }

            setTimeout(function () {
              addBtn.disabled = false;
              update();
            }, 1500);
          })
          .catch(function (err) {
            console.error('[oddit-section-product-main] stack add to cart failed', err);
            addBtn.disabled = false;
            addBtn.removeAttribute('aria-busy');
            update();
            showError((err && err.message) || 'Something went wrong. Please try again.');
          });
      });
    }

    update();
  }

  /* -------------------------------------------------------------------
     Build Your Ear Stack — slider arrows + dot indicators
     ------------------------------------------------------------------- */
  function initStackSlider(section) {
    var slider = section.querySelector('.oddit-section-product-main__stack-slider');
    if (!slider) return;
    var track = slider.querySelector('[data-oddit-stack-track]');
    if (!track) return;
    var prevBtn = section.querySelector('[data-oddit-stack-prev]');
    var nextBtn = section.querySelector('[data-oddit-stack-next]');
    var dotsContainer = section.querySelector('[data-oddit-stack-dots]');
    var cards = track.querySelectorAll('.oddit-section-product-main__stack-card');

    // Bleed slider to the viewport's right edge so cards aren't cut off,
    // matching the extras slider behavior.
    function applyBleed() {
      slider.style.marginRight = '0px';
      var rect = slider.getBoundingClientRect();
      var bleed = window.innerWidth - rect.right;
      slider.style.marginRight = (bleed > 0 ? -bleed : 0) + 'px';
    }
    applyBleed();
    window.addEventListener('resize', applyBleed);

    function getStops() {
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (max <= 1 || !cards || cards.length === 0) return [0];
      // Subtract `scroll-padding-left` so stops match the snap-aligned
      // scrollLeft a card lands on (snap-align: start + scroll-padding-left).
      var scrollPadLeft = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      var MIN_GAP = 60;
      var MAX_STOPS = 7;
      var stops = [0];
      for (var i = 1; i < cards.length; i++) {
        var pos = cards[i].offsetLeft - scrollPadLeft;
        if (pos <= 0) continue;
        if (pos >= max - MIN_GAP) break;
        stops.push(pos);
      }
      stops.push(max);
      // Cap total stops at MAX_STOPS by sampling evenly so arrow clicks and
      // visible dot count stay in sync.
      if (stops.length > MAX_STOPS) {
        var sampled = [];
        for (var s = 0; s < MAX_STOPS; s++) {
          var idx = Math.round((s * (stops.length - 1)) / (MAX_STOPS - 1));
          sampled.push(stops[idx]);
        }
        stops = sampled;
      }
      return stops;
    }

    function reachedStopIndex(stops) {
      var current = track.scrollLeft;
      var EPS = 4;
      for (var i = stops.length - 1; i >= 0; i--) {
        if (stops[i] <= current + EPS) return i;
      }
      return 0;
    }

    function rebuildDots() {
      if (!dotsContainer) return;
      var stops = getStops();
      var idx = reachedStopIndex(stops);
      // Only rebuild when stop count changes; otherwise just toggle active.
      if (dotsContainer.childElementCount !== stops.length) {
        dotsContainer.innerHTML = '';
        for (var i = 0; i < stops.length; i++) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'oddit-section-product-main__stack-dot';
          btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          btn.dataset.stopIndex = String(i);
          btn.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><circle cx="4.8" cy="4.8" r="4.8" fill="currentColor"/></svg>';
          dotsContainer.appendChild(btn);
        }
        if (!dotsContainer.dataset.clickWired) {
          dotsContainer.addEventListener('click', function (e) {
            var t = e.target.closest('.oddit-section-product-main__stack-dot');
            if (!t) return;
            var stopIdx = parseInt(t.dataset.stopIndex || '0', 10);
            var currentStops = getStops();
            var target = currentStops[stopIdx];
            if (typeof target === 'number') {
              track.scrollTo({ left: target, behavior: 'smooth' });
            }
          });
          dotsContainer.dataset.clickWired = '1';
        }
      }
      var dots = dotsContainer.children;
      for (var j = 0; j < dots.length; j++) {
        if (j === idx) {
          dots[j].classList.add('is-active');
          dots[j].setAttribute('aria-current', 'true');
        } else {
          dots[j].classList.remove('is-active');
          dots[j].removeAttribute('aria-current');
        }
      }
    }

    function updateState() {
      var max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 1;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= max - 1;
      rebuildDots();
    }

    function scrollByPage(direction) {
      var stops = getStops();
      var current = track.scrollLeft;
      var EPS = 8;
      var target;
      if (direction > 0) {
        target = stops[stops.length - 1];
        for (var i = 0; i < stops.length; i++) {
          if (stops[i] > current + EPS) { target = stops[i]; break; }
        }
      } else {
        target = stops[0];
        for (var j = stops.length - 1; j >= 0; j--) {
          if (stops[j] < current - EPS) { target = stops[j]; break; }
        }
      }
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { scrollByPage(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { scrollByPage(1); });
    if (dotsContainer) {
      dotsContainer.addEventListener('click', function (e) {
        var dot = e.target.closest('.oddit-section-product-main__stack-dot');
        if (!dot) return;
        var idx = parseInt(dot.dataset.stopIndex, 10);
        if (isNaN(idx)) return;
        var stops = getStops();
        if (idx < 0 || idx >= stops.length) return;
        track.scrollTo({ left: stops[idx], behavior: 'smooth' });
      });
    }
    track.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);

    // Recompute once images are loaded — the track's scrollWidth is unreliable
    // at init when images haven't measured yet, which collapses dots to one.
    if (document.readyState === 'complete') {
      updateState();
    } else {
      window.addEventListener('load', updateState, { once: true });
    }
    if (typeof ResizeObserver === 'function') {
      var trackObserver = new ResizeObserver(updateState);
      trackObserver.observe(track);
    }

    updateState();
  }

  /* -------------------------------------------------------------------
     Mobile sticky add-to-cart — visibility + edit-link scroll.
     Shown after the in-page ATC scrolls out of view; hidden again once
     the footer enters the viewport. Mobile-only via CSS @media gate;
     this script no-ops cheaply on desktop because the IntersectionObserver
     callback just toggles a class that has no visual effect there.
     ------------------------------------------------------------------- */
  function initStickyAtc(section) {
    var bar = section.querySelector('[data-oddit-sticky-atc]');
    if (!bar) return;
    if (typeof IntersectionObserver !== 'function') return;

    var inPageBtn = section.querySelector(
      '.oddit-section-product-main__add-to-cart[data-add-to-cart]:not(.oddit-section-product-main__add-to-cart--sticky)'
    );
    var footer = document.querySelector('.oddit-section-footer');

    // Only true once the in-page ATC has scrolled above the top of the viewport.
    // Starts false so the bar stays hidden on first load when the ATC is still below the fold.
    var atcPassed = false;
    var footerInView = false;

    function update() {
      var shouldShow = atcPassed && !footerInView;
      bar.classList.toggle('is-visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    if (inPageBtn) {
      var atcObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        atcPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        update();
      }, { threshold: 0 });
      atcObserver.observe(inPageBtn);
    }

    if (footer) {
      var footerObserver = new IntersectionObserver(function (entries) {
        footerInView = entries[0].isIntersecting;
        update();
      }, { threshold: 0 });
      footerObserver.observe(footer);
    }

    var editBtn = section.querySelector('[data-oddit-sticky-atc-edit]');
    if (editBtn) {
      editBtn.addEventListener('click', function () {
        var picker = section.querySelector('.oddit-section-product-main__variant');
        if (!picker) return;
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var firstInput = picker.querySelector('input[type="radio"]');
        if (firstInput) {
          // Defer focus until the smooth-scroll has had a chance to start;
          // focusing immediately would otherwise jump-scroll and cancel it.
          setTimeout(function () { firstInput.focus(); }, 400);
        }
      });
    }

    update();
  }

  /* -------------------------------------------------------------------
     TL-104: PDP image zoom lightbox.
     Any [data-oddit-zoom-trigger] click (hero image or non-video thumb)
     swaps its data-zoom-src into the modal <img> and reveals it. Close
     via the X, backdrop, image click, or Escape key.
     ------------------------------------------------------------------- */
  function initImageZoom(section) {
    var modal = section.querySelector('[data-oddit-zoom-modal]');
    if (!modal) return;
    var image = modal.querySelector('[data-oddit-zoom-image]');
    var video = modal.querySelector('[data-oddit-zoom-video]');
    var iframe = modal.querySelector('[data-oddit-zoom-iframe]');
    var triggers = section.querySelectorAll('[data-oddit-zoom-trigger]');

    // Pause + clear any in-flight video/iframe so opening an image
    // doesn't leave audio playing or the previous video element visible.
    function stopMedia() {
      if (video) {
        try { video.pause(); } catch (e) {}
        video.removeAttribute('src');
        try { video.load(); } catch (e) {}
        video.hidden = true;
      }
      if (iframe) {
        iframe.removeAttribute('src');
        iframe.hidden = true;
      }
    }

    var ZOOM_SCALE = 2;
    var isZoomed = false;

    function resetZoom() {
      isZoomed = false;
      image.style.transform = '';
      image.style.transformOrigin = '';
      image.style.cursor = 'zoom-in';
    }

    function applyZoomOrigin(e) {
      var rect = image.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      // Clamp to keep the visible region inside the image.
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(100, y));
      image.style.transformOrigin = x + '% ' + y + '%';
    }

    function toggleZoom(e) {
      if (isZoomed) {
        resetZoom();
        return;
      }
      applyZoomOrigin(e);
      image.style.transform = 'scale(' + ZOOM_SCALE + ')';
      image.style.cursor = 'zoom-out';
      isZoomed = true;
    }

    image.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleZoom(e);
    });
    image.addEventListener('mousemove', function (e) {
      if (!isZoomed) return;
      applyZoomOrigin(e);
    });
    image.addEventListener('mouseleave', function () {
      // Keep the user's pan position on mouseleave so re-entering the
      // image at the same spot doesn't jolt. Just bail.
    });

    function open(src, alt) {
      if (!src) return;
      stopMedia();
      resetZoom();
      image.src = src;
      image.alt = alt || '';
      image.hidden = false;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }
    function close() {
      stopMedia();
      resetZoom();
      modal.hidden = true;
      document.body.style.overflow = '';
      image.src = '';
      image.hidden = false; // reset for next image open
    }

    Array.prototype.forEach.call(triggers, function (trigger) {
      var src = trigger.getAttribute('data-zoom-src');
      var alt = trigger.getAttribute('data-zoom-alt');
      if (!src) return;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open(src, alt);
      });
      if (trigger.tagName !== 'BUTTON') {
        trigger.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open(src, alt);
          }
        });
      }
    });

    modal.querySelectorAll('[data-oddit-zoom-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  /* -------------------------------------------------------------------
     TL-107: PDP video playback via the zoom modal.
     Each [data-oddit-video-trigger] click swaps the modal into either a
     native <video> with the MP4 source, or an <iframe> with a
     YouTube/Vimeo embed URL. Close re-uses the shared zoom-modal close
     handlers (which also pause/clear the video on close).
     ------------------------------------------------------------------- */
  function initVideoPlayback(section) {
    var modal = section.querySelector('[data-oddit-zoom-modal]');
    if (!modal) return;
    var image = modal.querySelector('[data-oddit-zoom-image]');
    var video = modal.querySelector('[data-oddit-zoom-video]');
    var iframe = modal.querySelector('[data-oddit-zoom-iframe]');
    var triggers = section.querySelectorAll('[data-oddit-video-trigger]');

    function open(src, embed, poster) {
      // Reset everything before showing the next media so we can never
      // have two media elements visible (or two videos playing) at once.
      if (image) {
        image.hidden = true;
        image.src = '';
      }
      if (video) {
        try { video.pause(); } catch (e) {}
        video.removeAttribute('src');
        try { video.load(); } catch (e) {}
        video.hidden = true;
      }
      if (iframe) {
        iframe.removeAttribute('src');
        iframe.hidden = true;
      }
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      if (embed) {
        if (iframe) {
          iframe.src = embed;
          iframe.hidden = false;
        }
      } else if (src) {
        if (video) {
          video.src = src;
          if (poster) video.poster = poster;
          video.hidden = false;
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }
      }
    }

    function reset() {
      if (image) {
        image.hidden = false;
        image.src = '';
      }
      if (video) {
        try { video.pause(); } catch (e) {}
        video.removeAttribute('src');
        try { video.load(); } catch (e) {}
        video.hidden = true;
      }
      if (iframe) {
        iframe.removeAttribute('src');
        iframe.hidden = true;
      }
    }

    Array.prototype.forEach.call(triggers, function (trigger) {
      var src = trigger.getAttribute('data-video-src') || '';
      var embed = trigger.getAttribute('data-video-embed') || '';
      var poster = trigger.getAttribute('data-video-poster') || '';
      if (!src && !embed) return;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open(src, embed, poster);
      });
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(src, embed, poster);
        }
      });
    });

    // Reset the video state whenever the modal closes (close buttons +
    // backdrop already have click handlers from initImageZoom).
    modal.querySelectorAll('[data-oddit-zoom-close]').forEach(function (el) {
      el.addEventListener('click', reset);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) reset();
    });
  }

  /* -------------------------------------------------------------------
     TL-103: Built-in low-stock notice. Shows "Almost gone — only N left"
     directly above the ATC when the selected variant is tracked, can't
     oversell, has stock, and is at or below the threshold. Reacts to
     variant:change so swapping color/size updates the count live.
     ------------------------------------------------------------------- */
  function initLowStockNotice(section) {
    var notice = section.querySelector('[data-low-stock-notice]');
    if (!notice) return;
    var textEl = notice.querySelector('[data-low-stock-template]');
    if (!textEl) return;
    var template = textEl.getAttribute('data-low-stock-template') || '';
    var threshold = parseInt(notice.getAttribute('data-low-stock-threshold'), 10) || 0;
    if (threshold <= 0) {
      notice.hidden = true;
      return;
    }

    function update(variant) {
      if (
        !variant ||
        variant.inventory_management !== 'shopify' ||
        variant.inventory_policy === 'continue'
      ) {
        notice.hidden = true;
        return;
      }
      var qty = Number(variant.inventory_quantity);
      if (!isFinite(qty) || qty <= 0 || qty > threshold) {
        notice.hidden = true;
        return;
      }
      textEl.textContent = template
        .replace(/\[count\]/g, String(qty))
        .replace(/QUANTITY_LEFT/g, String(qty));
      notice.hidden = false;
    }

    section.addEventListener('variant:change', function (e) {
      update(e.detail && e.detail.variant);
    });
  }

  function initNotifyMe(section) {
    var modal = section.querySelector('[data-notify-me-modal]');
    var trigger = section.querySelector('[data-notify-me-trigger]');
    if (!modal || !trigger) return;
    var form = modal.querySelector('[data-notify-me-form]');
    var variantSel = modal.querySelector('[data-notify-me-variant]');
    var emailInput = modal.querySelector('[data-notify-me-email]');
    var success = modal.querySelector('[data-notify-me-success]');
    var buyWrap = section.querySelector('[data-buy-buttons]');

    function open() {
      modal.hidden = false;
      if (emailInput) {
        setTimeout(function () { emailInput.focus(); }, 0);
      }
    }
    function close() {
      modal.hidden = true;
      if (success) success.hidden = true;
    }

    trigger.addEventListener('click', open);
    modal.querySelectorAll('[data-notify-me-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    // Listen for variant-change events the variant selector emits.
    section.addEventListener('variant:change', function (e) {
      if (!buyWrap) return;
      var variant = e.detail && e.detail.variant;
      // No variant resolved (e.g. unsupported combination) → treat as
      // unavailable so the notify-me CTA stays surfaced.
      if (!variant) {
        buyWrap.setAttribute('data-variant-available', 'false');
        return;
      }
      buyWrap.setAttribute(
        'data-variant-available',
        variant.available === false ? 'false' : 'true'
      );
      if (variantSel) variantSel.value = String(variant.id);
    });

    if (form) {
      form.addEventListener('submit', function (e) {
        var action = form.getAttribute('action');
        if (!action) {
          // No endpoint configured yet — fall back to mailto so the lead
          // is captured manually instead of disappearing.
          e.preventDefault();
          var email = emailInput ? emailInput.value : '';
          var variantId = variantSel ? variantSel.value : '';
          var subject = encodeURIComponent('Back in stock notification request');
          var body = encodeURIComponent(
            'Email: ' + email + '\nProduct: ' + document.title +
            '\nVariant ID: ' + variantId
          );
          window.location.href = 'mailto:hello@tinilux.com?subject=' + subject + '&body=' + body;
          if (success) success.hidden = false;
          return;
        }
        // Endpoint present — let the form POST normally. If the backend
        // returns JSON, individual hosts (Klaviyo, etc.) can be hooked
        // up here with fetch() once the contract is known.
      });
    }
  }

  /* -------------------------------------------------------------------
     Rating row → scroll to Judge.me reviews widget.
     Triggered from the star/count row above the price. We try a few
     selectors so the link works whether the widget block is mounted
     by Shopify Apps directly or wrapped in our own section.
     ------------------------------------------------------------------- */
  function initReviewsScroll(section) {
    var trigger = section.querySelector('[data-oddit-reviews-scroll]');
    if (!trigger) return;
    trigger.addEventListener('click', function () {
      var target =
        document.querySelector('.jdgm-rev-widg') ||
        document.querySelector('.jdgm-widget') ||
        document.querySelector('[data-judgeme-product-reviews]') ||
        document.querySelector('[id*="judge-me"]') ||
        document.querySelector('[id*="judgeme"]');
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* -------------------------------------------------------------------
     Init
     ------------------------------------------------------------------- */
  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();